// The facts the homepage's copy states, worked out from the demo template's kit
// (templateKit.json, refreshed by scripts/snapshot_landing_kit.py) rather than
// typed into the copy, so a figure on the page is always the one the cards
// beside it show. kitStory.test.js checks that the claims the headlines make
// still hold for the committed snapshot.

import { incomeByCategory, rankByProfit } from '../../lib/equipmentInsights';
import { calculateROI } from '../../lib/utils';
import { equipmentIncomeByLine } from '../../lib/jobFinancials';

const byName = (equipment, fragment) =>
  equipment.find((e) => e.name.toLowerCase().includes(fragment.toLowerCase()));

const withRoi = (item) =>
  item && { ...item, roi: calculateROI(item.total_income || 0, item.purchase_price || 0) };

// Lines of the template's job the animation leaves out, to keep it short.
// Dropped before the sums, so the estimate it shows still adds up.
export const WRAP_LEFT_OUT = ['Canon CN-E 24mm T1.5 L F', 'Matthews C-Stand with Gobo Arm', 'Dana Dolly Portable Dolly'];

// The booked job, finished: what each line credits the item that earned it,
// and that item before and after. A demo copy gives job lines fresh ids, so a
// line finds its item by name; a line with no item is left out.
function wrapStory(job, equipment) {
  if (!job) return null;
  const shown = { ...job, equipment: (job.equipment || []).filter((l) => !WRAP_LEFT_OUT.includes(l.name)) };
  const lines = equipmentIncomeByLine(shown)
    .map(({ item: line, lineTotal, income }) => {
      const item = equipment.find((e) => e.name === line.name);
      const days = (Number(line.scheduled_days) || 0) * (Number(line.quantity) || 0);
      return item && {
        name: line.name,
        days,
        lineTotal,
        income,
        item,
        before: withRoi(item),
        after: withRoi({
          ...item,
          total_income: (item.total_income || 0) + income,
          total_days: (item.total_days || 0) + days,
        }),
      };
    })
    .filter(Boolean);
  return lines.length ? { job: shown, lines } : null;
}

export function kitStory({ equipment, projection, job }) {
  const active = equipment.filter((e) => e.is_active !== false && !e.archived);
  const ranked = rankByProfit(equipment);
  const priciest = [...active].sort((a, b) => b.purchase_price - a.purchase_price)[0];
  const categories = incomeByCategory(equipment);
  const share = (name) => categories.find((c) => c.name === name)?.share || 0;

  // The pair of lights behind "Same work. Twice the price."
  const dear = withRoi(byName(equipment, 'SkyPanel'));
  const cheap = withRoi(byName(equipment, '600d'));

  return {
    portfolio: projection.portfolio,
    itemCount: active.length,
    // Active items that have earned back all they cost
    mvp: ranked[0],
    priciest: withRoi(priciest),
    lights: { dear, cheap },
    categories,
    topCategory: categories[0],
    cameraShare: share('Camera'),
    lightingShare: share('Lighting'),
    wrap: wrapStory(job, equipment),
  };
}
